import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import {
  AcmeCoOperationErrorIcon,
  NewNotificationIcon,
  NotIcon,
  SupportIcon,
  ThumbUpIcon,
} from "../../assets/icons";
import { useDispatch, useSelector } from "react-redux";
import { reqToGetdashboarddata } from "../../redux/slices.js/feedback/feedBackSlice";
import { routes } from "../../helpers/constants";
import { useNavigate } from "react-router-dom";
import FeedbackDetailsModal from "../../components/FeedbackDetailsModal";

const FeedbackDashboard = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const navigate = useNavigate();
  const dashboardData = useSelector(
    (state) => state.feedbackslice.dashboardData
  );
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      reqToGetdashboarddata({
        data: {},
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
  }, [dispatch]);

  // Stats data from dashboardData
  const stats = [
    {
      id: 1,
      title: "Total Feedback",
      value: dashboardData?.totalFeedback || 0,
      color: "text-blue-100",
    },
    {
      id: 2,
      title: "New Items",
      value: dashboardData?.newFeedbackToday || 0,
      color: "text-blue-100",
    },
    {
      id: 3,
      title: "Total Votes",
      value: dashboardData?.totalVotes || 0,
      color: "text-red",
    },
    {
      id: 4,
      title: "Average Rating",
      value: `${dashboardData?.averageRating || "0"}/10`,
      color: "text-green",
    },
  ];

  // Dynamic feedback items from dashboardData
  const feedbackItems =
    dashboardData?.latestFeedbacks?.map((feedback, index) => {
      // Map feedback type to icon and color
      const getTypeDetails = (type) => {
        switch (type) {
          case 1: // Bug Report
            return { icon: NotIcon, color: "bg-[#FF4D4E]" };
          case 2: // Feature Request
            return { icon: NewNotificationIcon, color: "bg-[#4285F4]" };
          default:
            return { icon: SupportIcon, color: "bg-[#52C41A]" };
        }
      };

      // Map priority to tag text
      const getPriorityTag = (priority) => {
        switch (priority) {
          case 1:
            return "Low";
          case 2:
            return "Medium";
          case 3:
            return "High";
          case 4:
            return "Critical";
          default:
            return "Nice to have";
        }
      };

      // Calculate days ago
      const getDaysAgo = (dateString) => {
        const created = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - created);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return `${diffDays} days ago`;
      };

      const typeDetails = getTypeDetails(feedback.type);

      return {
        id: feedback.id,
        icon: typeDetails.icon,
        title: feedback.title,
        tag: getPriorityTag(feedback.priority),
        company: feedback.user?.name || "Unknown User",
        daysAgo: getDaysAgo(feedback.createdAt),
        votes: parseInt(feedback.net_votes) || 0,
        color: typeDetails.color,
      };
    }) || [];

  // Feedback chart data from dashboardData
  const feedbackByTypeData = dashboardData?.feedbackByType || [];
  const labels = feedbackByTypeData.map((item) => item.name);
  const colors = ["#1E3360", "#4271D5", "#F8FFCF", "#FF6B6B", "#4ECDC4"];
  const FeedbackSeries = feedbackByTypeData.map((item) => item.count);

  const FeedbackOptions = {
    chart: {
      type: "pie",
      redrawOnParentResize: true,
      redrawOnWindowResize: true,
    },
    labels: labels,
    colors: colors,
    legend: {
      show: false,
      position: "bottom",
      labels: {
        useSeriesColors: false,
      },
    },
    stroke: {
      show: false,
    },
    tooltip: {
      theme: "light",
      enabled: false,
    },
    dataLabels: {
      enabled: false,
    },
  };

  // Status chart data from dashboardData
  const statusDistributionData = dashboardData?.statusDistribution || [];

  const chartOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      zoom: {
        enabled: false,
      },
      redrawOnParentResize: true,
      redrawOnWindowResize: true,
    },
    plotOptions: {
      bar: {
        borderRadius: 0,
        columnWidth: "30%",
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: false,
    },
    xaxis: {
      categories: statusDistributionData.map((item) => item.name),
      labels: {
        style: {
          colors: "#000",
          fontSize: "12px",
          fontFamily: "Rubik",
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (val) => val.toFixed(0),
        style: {
          colors: "#000",
          fontSize: "12px",
          fontFamily: "Rubik",
        },
      },
    },
    grid: {
      strokeDashArray: 4,
    },
    colors: ["#4285F4"],
    states: {
      normal: {
        filter: {
          type: "none",
        },
      },
      hover: {
        filter: {
          type: "none",
        },
      },
      active: {
        filter: {
          type: "none",
        },
      },
    },
    responsive: [
      {
        breakpoint: 1024,
        options: {
          plotOptions: { bar: { columnWidth: "25%" } },
          chart: { height: 320 },
        },
      },
      {
        breakpoint: 640,
        options: {
          plotOptions: { bar: { columnWidth: "18%" } },
          chart: { height: 300 },
          legend: { show: false },
        },
      },
    ],
  };

  const chartSeries = [
    {
      name: "Tasks",
      data: statusDistributionData.map((item) => item.count),
    },
  ];

  // Submission chart data from dashboardData
  const submissionTrendData = dashboardData?.submissionTrend || [];

  const chartData = {
    series: [
      {
        name: "Submissions",
        data: submissionTrendData.map((item) => item.count),
      },
    ],
    options: {
      chart: {
        type: "line",
        height: 350,
        zoom: { enabled: false },
        toolbar: {
          show: false,
        },
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
      },
      stroke: {
        curve: "straight",
        width: 2,
      },
      xaxis: {
        categories: submissionTrendData.map((item) => item.month),
        labels: {
          style: {
            colors: "#000",
            fontSize: "12px",
            fontFamily: "Rubik",
          },
        },
      },
      yaxis: {
        min: 0,
        max:
          Math.max(...submissionTrendData.map((item) => item.count), 0) +
          (Math.max(...submissionTrendData.map((item) => item.count), 0) > 0
            ? 50
            : 100),
        tickAmount: 4,
        labels: {
          style: {
            colors: "#000",
            fontSize: "12px",
            fontFamily: "Rubik",
          },
        },
      },
      states: {
        normal: {
          filter: {
            type: "none",
          },
        },
        hover: {
          filter: {
            type: "none",
          },
        },
        active: {
          filter: {
            type: "none",
          },
        },
      },
    },
  };

  useEffect(() => {
    const t = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 120);
    return () => clearTimeout(t);
  }, []);

  const handleOpenModal = (item) => {
    setSelectedFeedback(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFeedback(null);
  };

  return (
    <>
      <div className="space-y-5">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {stats.map((item) => (
            <div key={item.id} className="px-5 py-6 border rounded-[10px]">
              <h4 className="text-sub_span !font-medium text-black">
                {item.title}
              </h4>
              <p className={`text-p !font-semibold mb-0 ${item.color}`}>
                {item.value}
              </p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          <div className="chart-card w-full">
            <h2 className="text-span text-black !font-semibold mb-0">
              Feedback by type{" "}
            </h2>
            <div className="w-full">
              <Chart
                options={FeedbackOptions}
                series={FeedbackSeries}
                type="pie"
                height={250}
                width="100%"
                style={{ width: "100%" }}
              />
            </div>
            <div className="text-sm ml-4">
              {labels.map((label, index) => (
                <div
                  key={index}
                  className="mb-2 flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-4 h-4"
                      style={{ backgroundColor: colors[index] || "#CCCCCC" }}
                    ></div>
                    <span className="text-span text-black">{label}</span>
                  </div>
                  <span className="text-span text-black">
                    {FeedbackSeries[index]} tenants
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="chart-card w-full">
            <h2 className="text-span text-black !font-semibold mb-0">
              Status Distribution
            </h2>
            <div className="w-full">
              <Chart
                options={chartOptions}
                series={chartSeries}
                type="bar"
                height={350}
                width="100%"
                style={{ width: "100%" }}
              />
            </div>
          </div>
          <div className="chart-card w-full md:col-span-2 xl:col-span-1">
            <h2 className="text-span text-black !font-semibold mb-0">
              Submission trend
            </h2>
            <div className="w-full">
              <Chart
                options={chartData.options}
                series={chartData.series}
                type="line"
                height={350}
                width="100%"
                style={{ width: "100%" }}
              />
            </div>
          </div>
          <div className="chart-card w-full md:col-span-2 xl:col-span-3">
            <div className="flex items-center justify-between flex-wrap gap-2 border-b-[1px] border-[D3D6DA3B] pb-[10px]">
              <h3 className="text-span text-black !font-semibold mb-0 p-0">
                Recent Feedback
              </h3>
              <div>
                <span
                  className="text-sub_span !font-bold text-black cursor-pointer"
                  onClick={() =>
                    navigate(routes.feedbackTriage + "?tab=Feedback list")
                  }
                >
                  View all
                </span>
              </div>
            </div>
            <div className="mt-2">
              {feedbackItems.length > 0 ? (
                feedbackItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex gap-3 items-center justify-between py-5 border-b-[1px] border-[#F5F6F6] flex-wrap last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-[40px] h-[40px] flex-none comman-svg flex items-center justify-center rounded-full text-white ${item.color}`}
                      >
                        <span className="flex-none">{item.icon}</span>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-span text-black !font-semibold mb-0">
                            {item.title}
                          </h3>
                          <span className="text-body bg-[#F9F9F9] text-black p-[10px] rounded-[10px]">
                            {item.tag}
                          </span>
                        </div>
                        <p className="text-span text-dark mb-0">
                          {item.company}{" "}
                          <span className="ml-2 pl-2 border-l-[1px] border-[#D3D6DA3B]">
                            {item.daysAgo}
                          </span>
                        </p>
                      </div>
                    </div>

                    {/* Right side: votes + view */}
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-1 border-[1px] border-[#FAFAFA] rounded-[4px] min-w-[73px] py-3 px-2 justify-center text-sub_span text-black !font-normal">
                        {ThumbUpIcon}
                        {item.votes}
                      </div>
                      <button
                        className="flex items-center gap-1 border-[1px] border-[#FAFAFA] rounded-[4px] min-w-[73px] py-3 px-2 justify-center text-sub_span text-black !font-normal"
                        onClick={() => handleOpenModal(item)}
                      >
                        View
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-10">
                  <p className="text-span text-dark">No feedback available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <FeedbackDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        feedbackItem={selectedFeedback}
      />
    </>
  );
};

export default FeedbackDashboard;
